import { Box } from "@mantine/core";
import { IconSend } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import type {
  ColDef,
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
  RowClickedEvent,
  ValueGetterParams,
} from "ag-grid-enterprise";
import dayjs from "dayjs";
import { useEffect, useEffectEvent, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { AgGridTable } from "~/components/ag-grid-table";
import { useBoolean } from "~/hooks/use-boolean";
import { trpc } from "~/lib/trpc";
import { RutAnswerModal } from "./-components/rut-answer-modal";
import { type RutQuestion, RutQuestionDrawer } from "./-components/rut-question-drawer";
import { RutQuestionStatus } from "./-components/rut-question-status";

const sendAnswersIcon = renderToStaticMarkup(<IconSend size={14} />);

export const Route = createFileRoute("/_authenticated/accounting/rut-questions")({
  component: RouteComponent,
  loader: async ({ context: { queryClient, trpc } }) => {
    await queryClient.prefetchQuery(trpc.rut.question.getQuestions.queryOptions());
  },
});

function RouteComponent() {
  const questions = useQuery(trpc.rut.question.getQuestions.queryOptions());
  const [selectedQuestion, setSelectedQuestion] = useState<RutQuestion | null>(null);
  const sendAnsModal = useBoolean();

  const updateSelectedQuestion = useEffectEvent(() => {
    if (!selectedQuestion) return;
    setSelectedQuestion((prev) => {
      if (!prev) return null;
      const updated = questions.data?.find((q) => q.id === prev.id);
      return updated || null;
    });
  });

  useEffect(() => {
    updateSelectedQuestion();
  }, [questions.data]);

  const defaultColDef = useMemo<ColDef<RutQuestion>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      enableRowGroup: true,
    }),
    [],
  );

  const colDefs = useMemo<ColDef<RutQuestion>[]>(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 80,
      },
      {
        field: "customerName",
        headerName: "Customer Name",
        width: 200,
      },
      {
        field: "customerPersonNumber",
        headerName: "Person Number",
        width: 160,
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice #",
        width: 140,
      },
      {
        field: "referenceNumber",
        headerName: "Reference Number",
        width: 180,
        enableRowGroup: true,
      },
      {
        field: "requestedAmount",
        headerName: "Requested Amount",
        width: 170,
        filter: "agNumberColumnFilter",
      },
      {
        field: "receivedOn",
        headerName: "Received On",
        width: 155,
        filter: "agDateColumnFilter",
        valueFormatter: ({ value }) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
      },
      {
        field: "deadline",
        headerName: "Deadline",
        width: 145,
        filter: "agDateColumnFilter",
        valueFormatter: ({ value }) => (value ? dayjs(value).format("YYYY-MM-DD") : ""),
      },
      {
        field: "status",
        headerName: "Status",
        width: 210,
        filter: "agSetColumnFilter",
        cellRenderer: RutQuestionStatus,
      },
      {
        colId: "representative",
        headerName: "Representative",
        valueGetter: (params: ValueGetterParams<RutQuestion>) => params.data?.representative?.name ?? "",
        width: 180,
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
      {
        colId: "type",
        headerName: "Type",
        valueGetter: (params: ValueGetterParams<RutQuestion>) =>
          params.data ? (params.data.previouslyApprovedAmount ? "Reopened" : "New") : null,
        width: 180,
        filter: "agSetColumnFilter",
        enableRowGroup: true,
      },
    ],
    [],
  );

  return (
    <Box>
      <AgGridTable
        tableName="rut-questions"
        persistView
        rowData={questions.data}
        columnDefs={colDefs}
        defaultColDef={defaultColDef}
        groupDisplayType="multipleColumns"
        onRowClicked={(e: RowClickedEvent<RutQuestion>) => {
          if (e.data) setSelectedQuestion(e.data);
        }}
        getContextMenuItems={(params: GetContextMenuItemsParams<RutQuestion>): (DefaultMenuItem | MenuItemDef)[] => [
          "copy",
          "separator",
          "export",
          {
            name: "Send answers",
            icon: sendAnswersIcon,
            action: () => sendAnsModal.open(),
          },
        ]}
      />
      <RutQuestionDrawer selectedQuestion={selectedQuestion} onClose={() => setSelectedQuestion(null)} />
      <RutAnswerModal opened={sendAnsModal.state} onClose={sendAnsModal.close} questions={questions.data ?? []} />
    </Box>
  );
}
